<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const router = useRouter();

const products = ref([]);
const loading = ref(true);
const error = ref('');
const search = ref('');
const total = ref(0);

const filteredProducts = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return products.value;
  return products.value.filter(p => p.title?.toLowerCase().includes(q));
});

function priceLabel(p) {
  const min = p.minSalePrice;
  const max = p.maxSalePrice;
  if (!min) return null;
  return min === max
    ? `${min.toFixed(2)} ₺`
    : `${min.toFixed(2)} – ${max.toFixed(2)} ₺`;
}

function goToDetail(product) {
  router.push({
    name: 'b2b_detail',
    params: { accountId: route.params.accountId, productId: product.id },
  });
}

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(
      route.params.accountId,
      'b2b_products'
    );
    products.value = data.products || [];
    total.value = data.total || 0;
  } catch (e) {
    error.value = 'Ürünler yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

const importing = ref(false);
const importMsg = ref('');
async function importAll() {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      "Ticimax kataloğundaki tüm ürünler Ürün Merkezi'ne aktarılsın mı? Zaten aktarılmış olanlara dokunulmaz."
    )
  )
    return;
  importing.value = true;
  importMsg.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      route.params.accountId,
      'product_center/import',
      {}
    );
    importMsg.value = `${res.imported} yeni ürün aktarıldı · Ürün Merkezi'nde toplam ${res.total}.`;
  } catch (e) {
    importMsg.value = 'Aktarım hatası: ' + e.message;
  } finally {
    importing.value = false;
  }
}

onMounted(loadData);
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div>
        <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Toptan Ürün Kataloğu
        </h1>
        <p class="text-xs text-slate-400 mt-0.5">
          Adrom Toptan · Ticimax
          <span v-if="total" class="ml-1">({{ total }} ürün)</span>
        </p>
        <p
          v-if="importMsg"
          class="text-xs text-woot-600 dark:text-woot-400 mt-1 font-medium"
        >
          {{ importMsg }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
          :disabled="importing"
          @click="importAll"
        >
          <span
            :class="
              importing ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-database'
            "
            class="size-3.5"
          />
          {{ importing ? 'Aktarılıyor…' : 'Ürün Merkezine Aktar' }}
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          @click="loadData"
        >
          <span class="i-lucide-refresh-cw size-3.5" />
          Yenile
        </button>
      </div>
    </div>

    <!-- Search -->
    <div
      class="px-6 py-3 shrink-0 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800"
    >
      <div
        class="flex items-center gap-2 max-w-md px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
      >
        <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
        <input
          v-model="search"
          type="text"
          placeholder="Ürün adı ile ara…"
          class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
        />
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto px-6 py-5">
      <!-- Loading (initial) -->
      <div
        v-if="loading"
        class="flex items-center justify-center h-48 text-slate-400 gap-2"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        <span class="text-sm">Ticimax kataloğu yükleniyor…</span>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
      >
        <span class="i-lucide-alert-circle size-5 shrink-0" />{{ error }}
      </div>

      <!-- Empty -->
      <div
        v-else-if="filteredProducts.length === 0"
        class="flex flex-col items-center justify-center h-48 text-slate-400 gap-2"
      >
        <span class="i-lucide-package size-10" />
        <span class="text-sm">Ürün bulunamadı</span>
      </div>

      <!-- Product grid -->
      <div
        v-else
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3"
      >
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          class="relative rounded-xl overflow-hidden cursor-pointer group shadow-sm hover:shadow-lg transition-all duration-200"
          @click="goToDetail(product)"
        >
          <!-- 2:3 image -->
          <div class="aspect-[2/3] bg-slate-100 dark:bg-slate-800">
            <img
              v-if="product.image"
              :src="product.image"
              :alt="product.title"
              class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              @error="$event.target.style.display = 'none'"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-slate-300"
            >
              <span class="i-lucide-image size-10" />
            </div>

            <!-- Gradient -->
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
            />

            <!-- Stock badge -->
            <div
              class="absolute top-2 right-2 px-1.5 py-0.5 rounded-md text-xs font-bold"
              :class="
                product.totalStock > 0
                  ? 'bg-green-500/90 text-white'
                  : 'bg-red-500/90 text-white'
              "
            >
              {{ product.totalStock }}
            </div>

            <!-- Info overlay -->
            <div class="absolute bottom-0 left-0 right-0 p-2.5">
              <p
                class="text-white text-xs font-semibold leading-tight line-clamp-2 drop-shadow"
              >
                {{ product.title }}
              </p>
              <p
                v-if="priceLabel(product)"
                class="text-white/80 text-xs mt-1 font-medium drop-shadow"
              >
                {{ priceLabel(product) }}
              </p>
            </div>

            <!-- Hover overlay -->
            <div
              class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <span
                class="bg-white/90 dark:bg-slate-900/90 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5 rounded-full shadow flex items-center gap-1.5"
              >
                <span class="i-lucide-eye size-3.5" />
                Detay Gör
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
